import { FIELD_PROPS, INNER_FIELD_PROPS } from '../constants'

// ==================== 搜索相关 ====================

/**
 * 查找下一个匹配项的 key
 * @param {Array} fields - 字段列表
 * @param {string} keyword - 搜索关键字
 * @param {string} currentKey - 当前高亮的 key
 * @returns {string} 匹配项的 key
 */
export function findNextMatch(fields, keyword, currentKey) {
  const flatList = flattenFields(fields)
  const matchIndices = []

  flatList.forEach((item, index) => {
    if (item[FIELD_PROPS.LABEL].indexOf(keyword) !== -1) {
      matchIndices.push(index)
    }
  })

  if (matchIndices.length === 0) return ''

  const currentIndex = flatList.findIndex(f => f[FIELD_PROPS.KEY] === currentKey)
  const currentMatchIndex = matchIndices.indexOf(currentIndex)

  if (currentMatchIndex === -1) {
    return flatList[matchIndices[0]][FIELD_PROPS.KEY]
  }

  const nextIndex = (currentMatchIndex + 1) % matchIndices.length
  return flatList[matchIndices[nextIndex]][FIELD_PROPS.KEY]
}


// ==================== 全选相关 ====================

/**
 * 设置所有字段的显示状态
 * @param {Array} fields - 字段列表
 * @param {boolean} val - 显示状态
 */
export function setAllShow(fields, val) {
  fields.forEach(item => {
    item[INNER_FIELD_PROPS.SHOW] = val
    if (item[INNER_FIELD_PROPS.CHILD]) {
      item[INNER_FIELD_PROPS.INDTERMINATE] = false
      setAllShow(item[INNER_FIELD_PROPS.CHILD], val)
    }
  })
}

/**
 * 扁平化字段列表
 * @param {Array} fields - 字段列表
 * @returns {Array} 扁平化后的字段列表
 */
export function flattenFields(fields) {
  const result = []
  fields.forEach(item => {
    result.push(item)
    if (item[INNER_FIELD_PROPS.CHILD]) {
      result.push(...flattenFields(item[INNER_FIELD_PROPS.CHILD]))
    }
  })
  return result
}

/**
 * 计算全选状态
 * @param {Array} allFields - 扁平化后的字段列表
 * @returns {Object} { checkAll, isIndeterminate }
 */
export function calcCheckStatus(allFields) {
  const checkedCount = allFields.filter(f => f[INNER_FIELD_PROPS.SHOW]).length
  const totalCount = allFields.length
  return {
    checkAll: checkedCount === totalCount && totalCount > 0,
    isIndeterminate: checkedCount > 0 && checkedCount < totalCount
  }
}



// ==================== 字段勾选联动 ====================

/**
 * 设置字段及其所有子级的显示状态
 * @param {Object} field - 字段对象
 * @param {boolean} val - 显示状态
 */
function setShowWithChildren(field, val) {
  const children = field[INNER_FIELD_PROPS.CHILD]
  field[INNER_FIELD_PROPS.SHOW] = val
  if (Array.isArray(children) && children.length > 0) {
    children.forEach(child => setShowWithChildren(child, val))
    const status = calcChildrenStatus(children)
    field[INNER_FIELD_PROPS.SHOW] = status === 1
    field[INNER_FIELD_PROPS.INDTERMINATE] = status === 0
  }
}

/**
 * 根据子级状态计算父级显示状态
 * @param {Array} children - 子级字段列表
 * @returns {number} -1: 全未选, 0: 半选, 1: 全选
 */
function calcChildrenStatus(children) {
  if (!Array.isArray(children) || children.length === 0) return -1

  let hasChecked = false
  let hasUnchecked = false

  for (const child of children) {
    const childStatus = getFieldCheckStatus(child)
    if (childStatus === 0) {
      return 0
    }
    if (childStatus === 1) {
      hasChecked = true
    } else {
      hasUnchecked = true
    }
    if (hasChecked && hasUnchecked) {
      return 0
    }
  }

  return hasChecked ? 1 : -1
}

/**
 * 获取字段的勾选状态（含子级）
 * @param {Object} field - 字段对象
 * @returns {number} -1: 全未选, 0: 半选, 1: 全选
 */
function getFieldCheckStatus(field) {
  const children = field[INNER_FIELD_PROPS.CHILD]
  if (Array.isArray(children) && children.length > 0) {
    return calcChildrenStatus(children)
  }
  return field[INNER_FIELD_PROPS.SHOW] ? 1 : -1
}

/**
 * 更新父级字段的显示状态（根据子级状态）
 * @param {Array} fields - 当前层级的字段列表
 * @param {Object} targetField - 目标字段
 * @returns {boolean} 是否找到并更新了目标字段的父级
 */
function updateParentStatus(fields, targetField) {
  for (const field of fields) {
    const children = field[INNER_FIELD_PROPS.CHILD]
    if (Array.isArray(children) && children.length > 0) {
      // 检查是否是直接父级
      if (children.includes(targetField)) {
        const status = calcChildrenStatus(children)
        field[INNER_FIELD_PROPS.SHOW] = status === 1
        field[INNER_FIELD_PROPS.INDTERMINATE] = status === 0
        // 继续向上更新祖父级
        return true
      }
      // 递归查找子级
      const found = updateParentStatus(children, targetField)
      if (found) {
        // 子级中有更新，当前父级也需要更新
        const status = calcChildrenStatus(children)
        field[INNER_FIELD_PROPS.SHOW] = status === 1
        field[INNER_FIELD_PROPS.INDTERMINATE] = status === 0
        // 继续向上更新祖父级
        return true
      }
    }
  }
  return false
}

/**
 * 设置指定字段的显示状态，自动联动父子级
 * @param {Array} rootFields - 根字段列表
 * @param {Object} field - 目标字段
 * @param {boolean} val - 显示状态
 */
export function setFieldShow(rootFields, field, val) {
  setShowWithChildren(field, val)
  updateParentStatus(rootFields, field)
}

// ==================== 字段移动 ====================

/**
 * 移动根级字段位置
 * @param {Array} rootFields - 根字段列表
 * @param {Object} field - 目标字段
 * @param {number} targetIndex - 目标位置（从1开始）
 */
export function moveField(rootFields, field, targetIndex) {
  const currentIndex = rootFields.indexOf(field)
  if (currentIndex === -1) return

  const index = targetIndex - 1
  if (index < 0 || index >= rootFields.length || index === currentIndex) return

  rootFields.splice(currentIndex, 1)
  rootFields.splice(index, 0, field)
}



// WEBPACK FOOTER //
// ./src/public/components/kye-field-settings/data/field-operations.js